import { ofType } from "redux-observable";
import { from, forkJoin, of } from "rxjs";
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import type { RootEpic, RootState } from "../types";
import { fileActions } from "./fileSlice";
import { FolderService } from "@/services/FolderService";
import { UploadFileService } from "@/features/upload/services/UploadFileService";
import { mapApiToFolderRecord } from "@/features/file-management/pages/FilePage/utils/fileUtils";
import { isUserAdmin } from "@/utils/jwtHelper";
import { selectFolderRootId } from "../navigation/navigationSlice";

/**
 * Epic: Fetch files by folder ID
 * Handles pagination and maps API response to FileRecord format
 */
export const fetchFilesByFolderIdEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    ofType(fileActions.fetchFilesRequest.type),
    switchMap((action: ReturnType<typeof fileActions.fetchFilesRequest>) => {
      const { folderId, page, pageSize } = action.payload;

      // Don't call API with empty folderId
      if (!folderId || folderId.trim() === "") {
        console.error("[fetchFilesByFolderIdEpic] folderId is empty");
        return of(
          fileActions.fetchFilesFailure({ error: "Invalid folder ID" })
        );
      }

      return from(
        FolderService.Get.getFilesByFolderId(folderId, { page, pageSize })
      ).pipe(
        map((response: any) => {
          // Store current folderId in localStorage
          localStorage.setItem("folderId", folderId);

          // Map results to FileRecord format
          const files = (response.data.results || []);

          // Return intermediate success with files data
          return fileActions.fetchFilesIntermediate({
            files,
            folderId,
            pagination: {
              page: response.data.page,
              pageCount: response.data.pageCount,
              pageSize: response.data.pageSize,
              queryCount: response.data.queryCount,
              totalCount: response.data.totalCount,
              firstRowIndex:
                (response.data.page - 1) * response.data.pageSize + 1,
              lastRowIndex: Math.min(
                response.data.page * response.data.pageSize,
                response.data.queryCount
              ),
            },
          });
        }),
        catchError((error) => {
          console.error("[fetchFilesByFolderIdEpic] Error:", error);
          return of(
            fileActions.fetchFilesFailure({
              error: error?.message || "Failed to fetch files",
            })
          );
        })
      );
    })
  );

/**
 * Epic: Fetch storage usage for labels
 * Only triggered for admin users at root folder
 */
export const fetchStorageForLabelsEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    ofType(fileActions.fetchFilesIntermediate.type),
    withLatestFrom(state$),
    mergeMap(
      ([action, state]: [
        ReturnType<typeof fileActions.fetchFilesIntermediate>,
        RootState
      ]) => {
        const { files, folderId } = action.payload;

        const folderRootId = selectFolderRootId(state);
        const isAdmin = false;
        const isRootFolder = folderId === folderRootId;

        // Only fetch storage for admin at root folder
        if (!isAdmin || !isRootFolder) {
          return of(fileActions.fetchFilesSuccess(action.payload));
        }

        // Filter label items (folders)
        const labelItems = files.filter(
          (item: any) => item.type === "folder" && item.id
        );

        if (labelItems.length === 0) {
          return of(fileActions.fetchFilesSuccess(action.payload));
        }

        const companyId = localStorage.getItem("companyId") || "";

        // Create storage requests for all labels
        const storageRequests = labelItems.map((item: any) =>
          from(
            UploadFileService.Get.getCompanyStorageUsage(companyId, item.id)
          ).pipe(
            map((storageInfo) => ({ labelId: item.id, storageInfo })),
            catchError((err) => {
              console.warn(
                `[fetchStorageForLabelsEpic] Failed to fetch storage for label ${item.id}:`,
                err
              );
              return of({ labelId: item.id, storageInfo: null });
            })
          )
        );

        // Fetch all storage data in parallel
        return forkJoin(storageRequests).pipe(
          map((storageResults: any[]) => {
            // Create a map of labelId to storage info
            const storageMap = new Map(
              storageResults.map((r) => [r.labelId, r.storageInfo])
            );

            // Update files with storage info
            const updatedFiles = files.map((item: any) => {
              if (
                item.type === "folder" &&
                item.id &&
                storageMap.has(item.id)
              ) {
                const storageInfo = storageMap.get(item.id);
                return {
                  ...item,
                  size: storageInfo?.storageSize || 0,
                  storageInfo,
                };
              }
              return item;
            });

            // Return final success with updated files
            return fileActions.fetchFilesSuccess({
              ...action.payload,
              files: updatedFiles,
            });
          }),
          catchError((error) => {
            console.error("[fetchStorageForLabelsEpic] Error:", error);
            // On error, return success with original files
            return of(fileActions.fetchFilesSuccess(action.payload));
          })
        );
      }
    )
  );

/**
 * Main epic: Orchestrates the file fetching flow
 * Combines fetchFilesByFolderIdEpic and fetchStorageForLabelsEpic
 */
export const fileEpics = [fetchFilesByFolderIdEpic, fetchStorageForLabelsEpic];
